(function () {
  'use strict';

  var root = document.documentElement;

  function setVar(name, value) {
    root.style.setProperty(name, value);
  }

  class EbHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header') || this.parentElement;
      this.stickyType = this.getAttribute('data-sticky') || 'none';
      this.revealOffset = parseInt(this.getAttribute('data-scroll-offset'), 10) || 0;
      this.lastScroll = window.pageYOffset;
      this.ticking = false;

      this.track = this.querySelector('[data-eb-track]');
      this.scrollButtons = Array.prototype.slice.call(this.querySelectorAll('[data-eb-scroll]'));
      this.searchToggle = this.querySelector('[data-eb-search-toggle]');
      this.searchClose = this.querySelector('[data-eb-search-close]');
      this.searchInput = this.querySelector('.eb-search__input');

      this.measure();
      this.enableSticky();
      this.initSearch();
      this.initScrollArrows();
      this.initDragScroll();

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    measure() {
      var height = Math.round(this.getBoundingClientRect().height);
      setVar('--eb-header-height', height + 'px');
      this.updateMegaTop();
    }

    updateMegaTop() {
      var bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      setVar('--eb-mega-top', bottom + 'px');
    }

    enableSticky() {
      if (this.stickyType === 'none' || !this.section) return;
      this.section.classList.add('eb-is-sticky');
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(this.handleScroll.bind(this));
    }

    handleScroll() {
      this.ticking = false;
      var y = window.pageYOffset;
      this.updateMegaTop();

      if (this.stickyType === 'none' || !this.section) {
        this.lastScroll = y;
        return;
      }

      var scrolledPast = y > this.revealOffset;

      if (this.stickyType === 'reduce-logo' || this.stickyType === 'top-only') {
        this.classList.toggle('eb-scrolled', scrolledPast);
        if (scrolledPast) this.measure();
      }

      if (this.stickyType === 'on-scroll-up') {
        var goingDown = y > this.lastScroll;
        if (goingDown && y > this.revealOffset) {
          this.section.classList.add('eb-is-hidden');
          this.closeMenus();
          this.closeSearch();
        } else if (!goingDown) {
          this.section.classList.remove('eb-is-hidden');
        }
      }

      this.lastScroll = y;
    }

    onResize() {
      this.measure();
      this.updateScrollArrows();
    }

    initSearch() {
      if (!this.searchToggle) return;

      this.searchToggle.addEventListener('click', function () {
        this.openSearch();
      }.bind(this));

      if (this.searchClose) {
        this.searchClose.addEventListener('click', this.closeSearch.bind(this));
      }

      this._onDocClickSearch = function (event) {
        if (!this.classList.contains('eb-header--search-open')) return;
        if (!this.contains(event.target)) this.closeSearch();
      }.bind(this);
      document.addEventListener('click', this._onDocClickSearch);

      this.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && this.classList.contains('eb-header--search-open')) {
          this.closeSearch();
          if (this.searchToggle) this.searchToggle.focus();
        }
      }.bind(this));
    }

    openSearch() {
      this.classList.add('eb-header--search-open');
      if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'true');
      if (this.searchInput) {
        var input = this.searchInput;
        window.requestAnimationFrame(function () {
          input.focus();
        });
      }
    }

    closeSearch() {
      if (!this.classList.contains('eb-header--search-open')) return;
      this.classList.remove('eb-header--search-open');
      if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'false');
    }

    initScrollArrows() {
      if (!this.track || !this.scrollButtons.length) return;

      this.scrollButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var dir = btn.getAttribute('data-eb-scroll') === 'prev' ? -1 : 1;
          var amount = Math.max(200, Math.round(this.track.clientWidth * 0.6));
          this.track.scrollBy({ left: dir * amount, behavior: 'smooth' });
        }.bind(this));
      }.bind(this));

      this._onTrackScroll = this.updateScrollArrows.bind(this);
      this.track.addEventListener('scroll', function () {
        window.requestAnimationFrame(this._onTrackScroll);
      }.bind(this), { passive: true });

      this.updateScrollArrows();
    }

    initDragScroll() {
      var track = this.track;
      if (!track) return;

      var isDown = false;
      var moved = false;
      var startX = 0;
      var startScroll = 0;
      var pointerId = null;

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return; // touch scrolls natively
        if (e.button !== 0) return;
        isDown = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        pointerId = e.pointerId;
      });

      track.addEventListener('pointermove', function (e) {
        if (!isDown) return;
        var dx = e.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) < 5) return; // threshold so plain clicks still register
          moved = true;
          track.classList.add('is-dragging');
          try { track.setPointerCapture(pointerId); } catch (err) {}
        }
        track.scrollLeft = startScroll - dx;
        e.preventDefault();
      });

      var end = function () {
        if (!isDown) return;
        isDown = false;
        if (moved) {
          try { track.releasePointerCapture(pointerId); } catch (err) {}
          window.requestAnimationFrame(function () {
            track.classList.remove('is-dragging');
          });
        }
        pointerId = null;
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);

      // Swallow the click that ends a drag so a pill/link doesn't navigate.
      track.addEventListener('click', function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      }, true);

      track.addEventListener('dragstart', function (e) {
        if (moved) e.preventDefault();
      });
    }

    updateScrollArrows() {
      if (!this.track || !this.scrollButtons.length) return;
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      var scrollable = maxScroll > 1;
      var x = this.track.scrollLeft;

      this.scrollButtons.forEach(function (btn) {
        var isPrev = btn.getAttribute('data-eb-scroll') === 'prev';
        var show = scrollable && (isPrev ? x > 1 : x < maxScroll - 1);
        btn.hidden = !show;
      });
    }

    closeMenus() {
      this.querySelectorAll('eb-header-menu').forEach(function (menu) {
        if (menu.close) menu.close();
      });
    }
  }

  var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  class EbHeaderMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.header = this.closest('eb-header');

      this.details.addEventListener('toggle', function () {
        this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
        if (this.details.open) this.onOpen();
      }.bind(this));

      if (hoverCapable) {
        this.addEventListener('mouseenter', this.open.bind(this));
        // Delayed close so crossing the gap to the fixed panel doesn't dismiss
        // it; re-entering the menu or panel cancels the pending close.
        this.addEventListener('mouseleave', this.scheduleClose.bind(this));
        this.addEventListener('focusin', this.open.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));

        // Suppress mouse clicks on the summary so they don't fight the hover
        // state (keyboard activation has detail 0 and is left alone).
        this.summary.addEventListener('click', function (event) {
          if (event.detail !== 0) event.preventDefault();
        });
      }

      this.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && this.details.open) {
          this.close();
          this.summary.focus();
        }
      }.bind(this));
    }

    onOpen() {
      document.querySelectorAll('eb-header-menu').forEach(function (menu) {
        if (menu !== this && menu.close) menu.close();
      }.bind(this));

      if (this.header && this.header.updateMegaTop) this.header.updateMegaTop();
      this.bindOutside();
    }

    onFocusOut() {
      window.setTimeout(function () {
        if (!this.contains(document.activeElement)) this.close();
      }.bind(this));
    }

    bindOutside() {
      if (this._onOutside) return;
      this._onOutside = function (event) {
        if (!this.contains(event.target)) this.close();
      }.bind(this);
      document.addEventListener('click', this._onOutside);
    }

    unbindOutside() {
      if (!this._onOutside) return;
      document.removeEventListener('click', this._onOutside);
      this._onOutside = null;
    }

    scheduleClose() {
      this.cancelClose();
      this._closeTimer = window.setTimeout(this.close.bind(this), 220);
    }

    cancelClose() {
      if (this._closeTimer) {
        window.clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
    }

    open() {
      this.cancelClose();
      if (this.details && !this.details.open) this.details.open = true;
    }

    close() {
      this.cancelClose();
      if (this.details && this.details.open) this.details.open = false;
      this.unbindOutside();
    }
  }

  if (!customElements.get('eb-header')) customElements.define('eb-header', EbHeader);
  if (!customElements.get('eb-header-menu')) customElements.define('eb-header-menu', EbHeaderMenu);
})();